import { firstValueFrom, Observable, tap } from 'rxjs';
import { environment } from './../../../../environments/environment';
import { ConfigService } from './../config/config.service';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ApiRequestOptions, ApiService } from '../api/api.service';
import { ApiErrorHandler } from '../api/api.error.handler';
import { PDFImageFor, PDFImgDataObject } from '../../../modules/shared/services/export.pdf.service';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { config } from 'src/app/config/config';

export interface ImageUploadResult {
  success: boolean;
  url: string;
  code?: string;
  msg?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {

  constructor(private _domSanitizer: DomSanitizer, 
    private _apiService: ApiService,
    private _apiErrorHandler: ApiErrorHandler) { }

  public safeImage(url: string): SafeUrl {
    return this._domSanitizer.bypassSecurityTrustUrl(url);
  }

  public imageSource(item: VisitorEvent | VisitorPlace)  {
    if (!item) {
      return config.serverDomain + config.defaultImageUrl;
    }
    let source = item.cover && item.cover.source;

    if (!source) {
      return config.serverDomain + config.defaultImageUrl;
    }
    if (item.cover.source.startsWith('assets/imgs/')) {     
      return config.serverDomain + item.cover.source;
    }

    if (item.cover.source.startsWith('data:')) {
      return item.cover.source;
    }
    
    if (!item.cover.source.startsWith('http')) {
      return config.imageServeUrl + item.cover.source;
    }

    return item.cover.source;
  }

  public imageSourceUrl(imageUrl: string, useLogoIfNoImage = true) {
    if (!imageUrl) {
      if (useLogoIfNoImage) {
        return this.defaultImageUrl();
      }
      return this.defaultImageUrl();
    }

    if (this.isBase64Image(imageUrl)) {
      return imageUrl;
    }

    if (imageUrl.startsWith('assets/imgs/')) {
      return environment.serverDomain + imageUrl;
    }

    if (!imageUrl.startsWith('http')) {
      // 'https://api.imgoingcalendar.com/imgs/' + imageUrl;
      return environment.imageServeUrl + imageUrl;
    }

    return imageUrl;
  }

  public defaultImageUrl() {
    return 'https://iti-images.s3.amazonaws.com/imgs/new_placeholder_image.jpg'; // environment.serverDomain + ConfigService.config.defaultImageUrl;
  }

  public uploadBase64ImageToAWS(
    imgBase64: string,
    maxWidth?: number | undefined,
    album?: string,
    useDefaultExt = false
  ): Observable<ImageUploadResult> {
    return this._apiService.post(`common/upload-img-to-aws`, {
      imgBase64,
      maxWidth,
      album,
      useDefaultExt
    }).pipe(tap((result: ImageUploadResult) => this.showUploadThreat(result)));
  }

  public uploadBase64ImageListToAWS(
    imgBase64List: string[],
    maxWidth?: number | undefined
  ): Observable<ImageUploadResult> {
    return this._apiService.post(`common/upload-imgs-to-aws`, {
      imgBase64List,
      maxWidth,
    }).pipe(tap((result: ImageUploadResult) => this.showUploadThreat(result)));
  }

  private showUploadThreat(result: ImageUploadResult): void {
    if (result && !result.success && result.code === 'malware_detected') {
      this._apiErrorHandler.showError(
        'This image could not be uploaded because a potential security threat was detected. Please choose another image.'
      );
    }
  }

  public async compressImageFile(file: File, width = 1200): Promise<File> {
    const base64 = await this.compressImageFileToBase64(file, width);
    let ext = 'jpg';
    if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
      ext = file.name.split('.')[1];
    }
    let imgFile = this.blobToFile(this.dataURLtoBlob(base64), `image.${ext}`);
    return imgFile;
  }

  public getImageFileSize(file: File): Promise<{ width: number; height: number; size: number }> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(null);
      }
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result.toString();

        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          resolve({
            width,
            height,
            size: file.size,
          });
        };
      };

      reader.readAsDataURL(file);

      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  public compressImageFileToBase64(file: File, width = 1200): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(null);
      }
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        let image = new Image();
        image.src = (e.target as any).result;
        image.onload = () => {
          let canvas = document.createElement('canvas'),
            context = canvas.getContext('2d'),
            data = '';

          let targetSize = this.compressImage(image, width);
          canvas.width = targetSize.targetWidth;
          canvas.height = targetSize.targetHeight;

          // Preserve transparency for PNG files
          if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
            // Clear canvas to ensure transparency is preserved
            context.clearRect(0, 0, canvas.width, canvas.height);
          }

          context.drawImage(image, 0, 0, targetSize.targetWidth, targetSize.targetHeight);
          
          // Use PNG format for PNG files to preserve transparency, JPEG for others
          let type = 'image/jpeg';
          if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
            type = 'image/png';
          } else if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
            const ext = file.name.split('.')[1];
            type = `image/${ext}`;
          }
          
          data = canvas.toDataURL(type);
          resolve(data);
        };
      };
    });
  }

  public compressImageFilesToBase64(files: File[], width = 1200): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (!files || files.length === 0) {
        reject(null);
      }
      let result: string[] = [];
      for (let index = 0; index < files.length; index++) {
        let reader = new FileReader();
        let file = files[index];
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          let image = new Image();
          image.src = (e.target as any).result;
          image.onload = () => {
            let canvas = document.createElement('canvas'),
              context = canvas.getContext('2d'),
              data = '';

            let targetSize = this.compressImage(image, width);
            canvas.width = targetSize.targetWidth;
            canvas.height = targetSize.targetHeight;

            // Preserve transparency for PNG files
            if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
              // Clear canvas to ensure transparency is preserved
              context.clearRect(0, 0, canvas.width, canvas.height);
            }

            context.drawImage(image, 0, 0, targetSize.targetWidth, targetSize.targetHeight);
            
            // Use PNG format for PNG files to preserve transparency, JPEG for others
            let type = 'image/jpeg';
            if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
              type = 'image/png';
            } else if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
              const ext = file.name.split('.')[1];
              type = `image/${ext}`;
            }
            
            data = canvas.toDataURL(type);
            result.push(data);
            if (index === files.length - 1) {
              resolve(result);
            }
          };
        };
      }
    });
  }

  // public compressImgFileToBase64(file: File, width = 800) {
  //   if (!file) {
  //     return of(null);
  //   }

  //   let reader = new FileReader();
  //   reader.readAsDataURL(file);
  //   reader.onload = (e) => {
  //     let image = new Image();
  //     image.src = (e.target as any).result;
  //     image.onload = () => {
  //       let canvas = document.createElement('canvas'),
  //         context = canvas.getContext('2d'),
  //         data = '';

  //       let targetSize = this.compressImage(image, width);
  //       canvas.width = targetSize.targetWidth;
  //       canvas.height = targetSize.targetHeight;

  //       context.drawImage(
  //         image,
  //         0,
  //         0,
  //         targetSize.targetWidth,
  //         targetSize.targetHeight
  //       );
  //       let type = 'image/jpeg',
  //         ext = 'jpg';
  //       if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
  //         ext = file.name.split('.')[1];
  //         type = `image/${ext}`;
  //       }
  //       data = canvas.toDataURL(type);
  //       resolve(data);
  //     };
  // }

  public getBase64ByImageFile(imageFile: File): Promise<any> {
    return new Promise((resolve, reject) => {
      if (imageFile) {
        try {
          let image = new Image();
          let FR = new FileReader();
          let canvas = document.createElement('canvas');
          let context = canvas.getContext('2d');

          FR.addEventListener('load', function (e) {
            image.src = (e as any).target.result;           
          });
          FR.readAsDataURL(imageFile);
          image.onload = () => {
            let targetSize = this.compressImage(image);

            canvas.width = targetSize.targetWidth;
            canvas.height = targetSize.targetHeight;
            
            // Preserve transparency for PNG files
            if (imageFile.type === 'image/png' || imageFile.name.toLowerCase().endsWith('.png')) {
              // Clear canvas to ensure transparency is preserved
              context.clearRect(0, 0, canvas.width, canvas.height);
            }
            
            context.drawImage(image, 0, 0, targetSize.targetWidth, targetSize.targetHeight);
            
            // Use the original file type to preserve transparency
            const outputType = imageFile.type || 'image/png';
            const data = canvas.toDataURL(outputType);
            resolve(data);
          };
        } catch (ex) {
          reject(ex);
        }
      } else {
        resolve(null);
      }
    });
  }

  public getBase64ImageByUrl(imgUrl, maxWidth?: number, quality?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!imgUrl) {
        resolve(null);
      }
      if (this.isBase64Image(imgUrl)) {
        resolve(imgUrl);
      } else {
        let img = document.createElement('img');
        img.crossOrigin = 'anonymous';
        img.onload = (x) => {
          let base64 = null;
          try {
            let image = x.srcElement as HTMLImageElement;
            // Use JPEG format for PDF exports to reduce file size, PNG for other uses
            const imageType = maxWidth ? 'image/jpeg' : 'image/png';
            base64 = this.getBase64Image(image, imageType, maxWidth, quality);
            resolve(base64);
          } catch (e) {
            reject(e);
          }
        };
        img.src = this.imageSourceUrl(imgUrl);
      }
    });
  }

  async loadImageBase64(imgUrl: string): Promise<string> {
    // image on the prod server cannot be loaded from dev server (under testing now), so load it from client side here
    if (imgUrl.indexOf(ConfigService.config.domainV2) >= 0) {
      const base64 = await this.getBase64ImageByUrl(imgUrl);
      return base64 ? base64.base64 : null;
    }
    // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
    return await this.getBase64FromServer(imgUrl).catch((ex) => {
      throw ex;
    });
  }

  calculateCenterCrop(
    imageWidth: number,
    imageHeight: number,
    targetWidth: number,
    targetHeight: number
  ) {
    const targetAspectRatio = targetWidth / targetHeight;
    const imageAspectRatio = imageWidth / imageHeight;

    let cropWidth: number;
    let cropHeight: number;
    let cropX: number;
    let cropY: number;

    if (imageAspectRatio > targetAspectRatio) {
      // Image is wider than target ratio
      cropHeight = imageHeight;
      cropWidth = imageHeight * targetAspectRatio;
      cropY = 0;
      cropX = (imageWidth - cropWidth) / 2;
    } else {
      // Image is taller than target ratio
      cropWidth = imageWidth;
      cropHeight = imageWidth / targetAspectRatio;
      cropX = 0;
      cropY = (imageHeight - cropHeight) / 2;
    }

    return { cropX, cropY, cropWidth, cropHeight };
  }

  async cropImage(imageUrl: string, targetWidth: number, targetHeight: number): Promise<string> {
    return new Promise(async (resolve, reject) => {
      const base64 = await this.loadImageBase64(imageUrl);
      const imageObj = new Image();
      imageObj.onload = () => {
        // Create a virtual canvas
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Set the desired output size
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Clear canvas to ensure transparency is preserved
        context?.clearRect(0, 0, canvas.width, canvas.height);

        const { cropX, cropY, cropWidth, cropHeight } = this.calculateCenterCrop(
          imageObj.width,
          imageObj.height,
          targetWidth,
          targetHeight
        );

        // Draw the cropped image
        context?.drawImage(
          imageObj,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          canvas.width,
          canvas.height
        );
        
        // Always use PNG format to preserve transparency
        const croppedBase64 = canvas.toDataURL('image/png');
        resolve(croppedBase64);
      };

      imageObj.onerror = (error) => {
        reject(error);
      };

      imageObj.src = base64;
    });
  }

  async loadImagesBase64InBatches(
    imgUrls: string[],
    maxWidth: number,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<Map<string, string | null>> {
    const batchSize = 20;
    const concurrency = 2;
    const maxRetries = 2;
    const maxRetryWaitSeconds = 10;
    const results = new Map<string, string | null>();
    const urls = Array.from(new Set((imgUrls || []).filter((url) => !!url && url.startsWith('http'))));
    const batches: string[][] = [];
    for (let i = 0; i < urls.length; i += batchSize) {
      batches.push(urls.slice(i, i + batchSize));
    }

    let loaded = 0;
    let isRateLimited = false;
    let isBatchEndpointMissing = false;
    const finishBatch = (batch: string[], base64ByUrl?: Map<string, string | null>) => {
      batch.forEach((url) => results.set(url, base64ByUrl?.get(url) || null));
      loaded += batch.length;
      onProgress?.(loaded, urls.length);
    };

    const requestBatch = async (batch: string[]): Promise<Map<string, string | null>> => {
      for (let attempt = 0; ; attempt++) {
        try {
          const options = Object.assign(new ApiRequestOptions(), {
            timeout: 120 * 1000,
            suppressErrorHandler: true,
          });
          const response = await firstValueFrom(
            this._apiService.post('common/images-base64', { urls: batch, maxWidth }, options)
          );
          if (!Array.isArray(response?.results)) {
            isBatchEndpointMissing = true;
            return null;
          }
          const base64ByUrl = new Map<string, string | null>();
          response.results.forEach((result) => {
            base64ByUrl.set(result.url, result.succeed ? result.base64 : null);
          });
          return base64ByUrl;
        } catch (error) {
          const status = error instanceof HttpErrorResponse ? error.status : 0;
          if (status === 404) {
            isBatchEndpointMissing = true;
            return null;
          }
          const retryAfterSeconds = Number(error?.error?.retryAfterSeconds) || 0;
          if (status === 429 && attempt < maxRetries && retryAfterSeconds <= maxRetryWaitSeconds) {
            await new Promise((resolve) => setTimeout(resolve, Math.max(1, retryAfterSeconds) * 1000));
            continue;
          }
          if (status === 429) {
            isRateLimited = true;
          }
          console.error('images-base64 batch failed:', error);
          return null;
        }
      }
    };

    const requestOneByOne = async (batch: string[]): Promise<Map<string, string | null>> => {
      const base64ByUrl = new Map<string, string | null>();
      for (const url of batch) {
        base64ByUrl.set(url, await this.getBase64FromServer(url).catch(() => null));
      }
      return base64ByUrl;
    };

    let nextBatchIndex = 0;
    const runners = Array.from({ length: Math.min(concurrency, batches.length) }, async () => {
      while (nextBatchIndex < batches.length) {
        const batch = batches[nextBatchIndex++];
        if (isRateLimited) {
          finishBatch(batch);
          continue;
        }
        const base64ByUrl = isBatchEndpointMissing
          ? await requestOneByOne(batch)
          : (await requestBatch(batch)) || (isBatchEndpointMissing ? await requestOneByOne(batch) : null);
        finishBatch(batch, base64ByUrl);
      }
    });
    await Promise.all(runners);
    return results;
  }

  private getBase64FromServer(imgUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!imgUrl) {
        resolve(null);
        return;
      }

      const url = this.imageSourceUrl(imgUrl);
      if (this.isBase64Image(url)) {
        resolve(url);
        return;
      }

      // images from Amazon S3 has CORS problems (have already set in the Amazon dashboard, but still not work), so here is to get the image base64 from server side
      if (url && url.startsWith('http')) {
        this._apiService.get(`common/image-base64?url=${encodeURIComponent(imgUrl)}`).subscribe({
          next: (data) => {
            let base64 = null;
            if (data && data.succeed) {
              base64 = data.base64;
            }
            resolve(base64);
          },
          error: (err) => {
            console.error('image-base64 load failed:', err);
            resolve(null);
          },
        });
      } else {
        reject('please check image url.');
      }
    });
  }

  private getBase64Image(img: HTMLImageElement, imageType = 'image/png', maxWidth?: number, quality?: number) {
    // Maximum dimensions for PDF images to reduce file size (if maxWidth is provided)
    let targetWidth = img.width;
    let targetHeight = img.height;
    
    if (maxWidth && (targetWidth > maxWidth || targetHeight > maxWidth)) {
      const aspectRatio = targetWidth / targetHeight;
      if (targetWidth > targetHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round(maxWidth / aspectRatio);
      } else {
        targetHeight = maxWidth;
        targetWidth = Math.round(maxWidth * aspectRatio);
      }
    }
    
    let canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    let ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    
    // Use quality parameter for JPEG compression if provided
    // PNG format doesn't support quality parameter
    const outputQuality = imageType === 'image/jpeg' && quality !== undefined ? quality : undefined;
    let dataURL = canvas.toDataURL(imageType, outputQuality);
    return dataURL;
  }

  public isBase64Image(url) {
    return url.startsWith('data:');
  }

  public getImageSize(base64: string): Promise<{ width; height }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };
      img.onerror = (e) => {
        console.log(`error when getImageSize, ${e}`);
        resolve({
          width: 0,
          height: 0,
        });
      };
      img.src = base64;
    });
  }

  public async blobToBase64(blob: Blob, maxWidth?: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          let imgSrc = reader.result as string;

          if (maxWidth) {
            const img = new Image();
            img.src = imgSrc;
            await new Promise((res) => (img.onload = res));

            if (img.width > maxWidth) {
              const canvas = document.createElement('canvas');
              const scale = maxWidth / img.width;
              canvas.width = maxWidth;
              canvas.height = img.height * scale;

              const ctx = canvas.getContext('2d');
              
              // Preserve transparency for PNG blobs
              if (blob.type === 'image/png') {
                // Clear canvas to ensure transparency is preserved
                ctx?.clearRect(0, 0, canvas.width, canvas.height);
              }
              
              ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              // Use PNG format for PNG blobs to preserve transparency
              const outputFormat = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
              imgSrc = canvas.toDataURL(outputFormat, 1);
            }
          }

          // Preserve the original format from the blob
          const format = blob.type || 'image/png';
          resolve('data:' + format + ';base64,' + imgSrc.split(',')[1]);
        } catch (e) {
          reject(e);
        }
      };
      reader.readAsDataURL(blob);
    });
  }

  private compressImage(image, width = 1200) {
    let originWidth = image.naturalWidth,
      originHeight = image.naturalHeight;

    //let maxWidth = 960, maxHeight = 960;
    let maxWidth = width,
      maxHeight = width * 0.8;
    let targetWidth = originWidth,
      targetHeight = originHeight;
    if (originWidth > maxWidth || originHeight > maxHeight) {
      if (originWidth / originHeight > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round(maxWidth * (originHeight / originWidth));
      } else {
        targetHeight = maxHeight;
        targetWidth = Math.round(maxHeight * (originWidth / originHeight));
      }
    }
    return {
      targetWidth,
      targetHeight,
    };
  }

  private dataURLtoBlob(imgBase64) {
    let arr = imgBase64.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  private blobToFile(theBlob, fileName): File {
    theBlob.lastModifiedDate = new Date();
    theBlob.lastModified = new Date();
    theBlob.name = fileName;
    return theBlob;
  }
}
